import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type {
  PortfolioRadarSignal,
  PortfolioRadarSignalSeverity,
  PortfolioRadarSignalType,
} from '../types';
import { db } from '../lib/firebase';
import { COLLECTION_PORTFOLIO_RADAR_SIGNALS } from '../lib/firestorePaths';

export type PortfolioRadarCategory = 'needs_attention' | 'follow_on_candidate' | 'missing_update';

export type PortfolioRadarSignalsState = {
  signals: PortfolioRadarSignal[];
  grouped: Record<PortfolioRadarCategory, PortfolioRadarSignal[]>;
  loading: boolean;
  error: string | null;
};

const EMPTY_STATE: PortfolioRadarSignalsState = {
  signals: [],
  grouped: {
    needs_attention: [],
    follow_on_candidate: [],
    missing_update: [],
  },
  loading: false,
  error: null,
};

const SIGNAL_TYPES: PortfolioRadarSignalType[] = [
  'missing_update',
  'runway_risk',
  'negative_trend',
  'follow_on_candidate',
  'founder_ask',
];

const SEVERITY_VALUES: PortfolioRadarSignalSeverity[] = ['info', 'watch', 'urgent'];

const SEVERITY_ORDER: Record<PortfolioRadarSignalSeverity, number> = {
  urgent: 0,
  watch: 1,
  info: 2,
};

export const PORTFOLIO_RADAR_CATEGORY_LABELS: Record<PortfolioRadarCategory, string> = {
  needs_attention: 'Needs attention',
  follow_on_candidate: 'Follow-on candidate',
  missing_update: 'Missing update',
};

function docItemId(d: QueryDocumentSnapshot, data: { id?: unknown }): string {
  if (typeof data.id === 'string' && data.id.trim()) return data.id.trim();
  const parts = d.id.split('__');
  return parts.length > 1 ? parts.slice(1).join('__') : d.id;
}

function asSignalType(value: unknown): PortfolioRadarSignalType {
  return SIGNAL_TYPES.includes(value as PortfolioRadarSignalType)
    ? (value as PortfolioRadarSignalType)
    : 'missing_update';
}

function asSeverity(value: unknown): PortfolioRadarSignalSeverity {
  return SEVERITY_VALUES.includes(value as PortfolioRadarSignalSeverity)
    ? (value as PortfolioRadarSignalSeverity)
    : 'info';
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

function docToPortfolioRadarSignal(d: QueryDocumentSnapshot): PortfolioRadarSignal {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const id = docItemId(d, x);

  return {
    id,
    userId: typeof x.userId === 'string' ? x.userId : '',
    companyId: typeof x.companyId === 'string' ? x.companyId : '',
    type: asSignalType(x.type),
    severity: asSeverity(x.severity),
    title: typeof x.title === 'string' ? x.title : 'Portfolio signal',
    reason: typeof x.reason === 'string' ? x.reason : '',
    sourceUpdateIds: asStringArray(x.sourceUpdateIds),
    route: typeof x.route === 'string' ? x.route : '/investments',
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
    updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : now,
  };
}

export function portfolioRadarCategory(signal: PortfolioRadarSignal): PortfolioRadarCategory {
  if (signal.type === 'missing_update') return 'missing_update';
  if (signal.type === 'follow_on_candidate') return 'follow_on_candidate';
  return 'needs_attention';
}

export function sortPortfolioRadarSignals(signals: PortfolioRadarSignal[]): PortfolioRadarSignal[] {
  return [...signals].sort((a, b) => {
    const severityDelta = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
    if (severityDelta !== 0) return severityDelta;
    return b.updatedAt.localeCompare(a.updatedAt);
  });
}

export function groupPortfolioRadarSignals(
  signals: PortfolioRadarSignal[]
): Record<PortfolioRadarCategory, PortfolioRadarSignal[]> {
  const grouped: Record<PortfolioRadarCategory, PortfolioRadarSignal[]> = {
    needs_attention: [],
    follow_on_candidate: [],
    missing_update: [],
  };

  for (const signal of sortPortfolioRadarSignals(signals)) {
    grouped[portfolioRadarCategory(signal)].push(signal);
  }

  return grouped;
}

export function usePortfolioRadarSignals(userId: string): PortfolioRadarSignalsState {
  const [signals, setSignals] = useState<PortfolioRadarSignal[]>([]);
  const [loading, setLoading] = useState(Boolean(userId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setSignals([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    const qSignals = query(
      collection(db, COLLECTION_PORTFOLIO_RADAR_SIGNALS),
      where('userId', '==', userId)
    );

    const applySignals = (docs: QueryDocumentSnapshot[]) => {
      setSignals(sortPortfolioRadarSignals(docs.map(docToPortfolioRadarSignal)));
    };

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const snap = await getDocs(qSignals);
        if (cancelled) return;

        applySignals(snap.docs);

        unsubscribe = onSnapshot(
          qSignals,
          (next) => applySignals(next.docs),
          (e) => setError(e.message)
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load portfolio radar signals');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [userId]);

  return useMemo(
    () => ({
      signals,
      grouped: groupPortfolioRadarSignals(signals),
      loading,
      error,
    }),
    [signals, loading, error]
  );
}

export function emptyPortfolioRadarSignalsState(): PortfolioRadarSignalsState {
  return EMPTY_STATE;
}
